/*
 * ReminderForm — shared "create / edit a task" form used by:
 *   • The Reminders modal on student & teacher profiles
 *   • The Reminders dashboard (new task button)
 *
 * UX notes:
 *   - Date is shown by default. Time is opt-in via "+ Add time".
 *   - Priority, assignee, and labels all use SearchableSelect with icons.
 *   - Labels auto-create on Enter inside the search box.
 *
 * Stays presentational — caller controls submit/save side effects.
 */

import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  FaRegCalendarAlt,
  FaRegBell,
  FaUserCircle,
  FaLink,
  FaRegStickyNote,
  FaExclamationTriangle,
  FaTag,
  FaPlus,
  FaTimes,
  FaFlag
} from 'react-icons/fa';

import { PRIORITY_OPTIONS } from './constants';
import {
  REMINDER_OFFSETS,
  list_team_members,
  list_labels,
  create_label,
  team_member_by_uuid,
  linked_user_by_uuid,
  search_linked_users,
  detect_conflicts
} from './api';
import SearchableSelect from './SearchableSelect';
import PriorityIcon from './PriorityIcon';
import './reminders.css';

// Title is auto-derived from the description (first line, capped at 80 chars
// at a word boundary). This keeps the form simple and matches how most
// inbox-style task tools work (Linear, Things, Apple Reminders).
const deriveTitle = (description) => {
  if (!description) return 'Untitled reminder';
  const firstLine = description.split('\n').find((l) => l.trim().length > 0) || description;
  const trimmed = firstLine.trim();
  if (trimmed.length <= 80) return trimmed;
  const cut = trimmed.slice(0, 80);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

const EMPTY = {
  description: '',
  due_date: moment().format('YYYY-MM-DD'),
  due_time: moment().add(1, 'hour').format('HH:mm'),
  has_time: false,
  priority: 'Medium',
  assignee_uuids: [],
  linked_user_uuid: '',
  label_uuids: [],
  alert_one: 15,
  alert_two: ''
};

function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
      {Icon ? <Icon className="text-gray-400" size={11} /> : null}
      {children}
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-mytPrimary focus:outline-none focus:ring-1 focus:ring-mytPrimary/40';

function ReminderForm({
  initial,
  lockedLinkedUser,
  defaultAssigneeUuid,
  selfUuid, // uuid of the logged-in team member — labelled "Self" in the assignee dropdown
  onSubmit,
  onCancel,
  submitLabel
}) {
  // "Save" works in both create and edit modes — keeps the CTA short and consistent.
  const effectiveSubmitLabel = submitLabel || 'Save';
  const [form, setForm] = useState(() => {
    // Decide the initial assignees: editing → carry forward; otherwise default
    // to "self" if we know who that is.
    const initialAssignees = Array.isArray(initial?.assignee_uuids)
      ? initial.assignee_uuids
      : defaultAssigneeUuid
        ? [defaultAssigneeUuid]
        : selfUuid
          ? [selfUuid]
          : [];
    return {
      ...EMPTY,
      ...(initial || {}),
      assignee_uuids: initialAssignees,
      linked_user_uuid: lockedLinkedUser?.uuid || (initial && initial.linked_user_uuid) || ''
    };
  });
  // Seed `team` with the self entry synchronously so the "Self" label shows
  // immediately, before the (mocked-async) team list resolves.
  const [team, setTeam] = useState(() => {
    const self = selfUuid ? team_member_by_uuid(selfUuid) : null;
    return self ? [self] : [];
  });
  // linkedUsers is the option pool for the linked-user picker. It is seeded
  // with the currently selected user (so the trigger renders a label when
  // editing) and refilled by an async typeahead while the user types.
  const [linkedUsers, setLinkedUsers] = useState(() => {
    const preset = lockedLinkedUser || linked_user_by_uuid(initial?.linked_user_uuid);
    return preset ? [preset] : [];
  });
  const [linkedQuery, setLinkedQuery] = useState('');
  // Toggle on the dashboard's reminder form between student and teacher search.
  // The profile modal locks the linked user, so this control is unused there.
  const [linkedRole, setLinkedRole] = useState(() => {
    const preset =
      lockedLinkedUser?.role ||
      linked_user_by_uuid(initial?.linked_user_uuid)?.role ||
      initial?.linked_user_role ||
      'student';
    return preset === 'teacher' ? 'teacher' : 'student';
  });
  const [linkedSearching, setLinkedSearching] = useState(false);
  const [labels, setLabels] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    list_team_members().then(setTeam);
    list_labels().then(setLabels);
  }, []);

  // Debounced linked-user typeahead. Fires once the query is ≥3 chars,
  // querying only the currently-selected role (student or teacher). The
  // currently-selected user is kept in the option pool so the trigger
  // renders a label while editing. `linkedSearching` drives the inline
  // "Searching…" hint and suppresses the "No matches" empty state.
  useEffect(() => {
    const q = linkedQuery.trim();
    if (q.length < 3) {
      setLinkedSearching(false);
      return undefined;
    }
    setLinkedSearching(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      search_linked_users(q, linkedRole)
        .then((rows) => {
          if (cancelled) return;
          const merged = [...rows];
          const selected = lockedLinkedUser || linked_user_by_uuid(form.linked_user_uuid) || null;
          if (selected && !merged.some((u) => u.uuid === selected.uuid)) {
            merged.unshift(selected);
          }
          setLinkedUsers(merged);
        })
        .finally(() => {
          if (!cancelled) setLinkedSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [linkedQuery, linkedRole, lockedLinkedUser, form.linked_user_uuid]);

  useEffect(() => {
    if (
      !form.has_time ||
      !Array.isArray(form.assignee_uuids) ||
      form.assignee_uuids.length === 0 ||
      !form.due_date ||
      !form.due_time
    ) {
      setConflicts([]);
      return;
    }
    const due_at = moment(`${form.due_date} ${form.due_time}`, 'YYYY-MM-DD HH:mm').toISOString();
    detect_conflicts({
      assignee_uuids: form.assignee_uuids,
      due_at,
      ignore_uuid: initial?.uuid
    }).then(setConflicts);
  }, [form.assignee_uuids, form.due_date, form.due_time, form.has_time, initial?.uuid]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!form.description.trim()) return;
    // Always send a full ISO timestamp — the form's `due_time` has a sane
    // default (now + 1 hour) even when has_time=false. has_time still
    // controls whether the UI surfaces the time, but the underlying
    // value is always meaningful so the tooltip / timezone math works.
    const due_at = moment(
      `${form.due_date} ${form.due_time || '09:00'}`,
      'YYYY-MM-DD HH:mm'
    ).toISOString();
    const alerts = [];
    if (form.alert_one !== '' && form.alert_one !== null && form.alert_one !== undefined) {
      alerts.push({ offset_minutes: Number(form.alert_one) });
    }
    if (form.alert_two !== '' && form.alert_two !== null && form.alert_two !== undefined) {
      alerts.push({ offset_minutes: Number(form.alert_two) });
    }
    const linkedUuid = lockedLinkedUser?.uuid || form.linked_user_uuid || null;
    // Resolve the linked user's role: locked-user prop wins; otherwise look up
    // the cached search result; fall back to the picker's role toggle.
    const linkedRoleResolved =
      lockedLinkedUser?.role ||
      (linkedUuid ? linked_user_by_uuid(linkedUuid)?.role : null) ||
      (linkedUuid ? linkedRole : null);
    const payload = {
      // Carry forward the uuid when editing so the caller knows it's an update
      // rather than a create. When creating, `uuid` will be undefined.
      uuid: initial?.uuid,
      title: deriveTitle(form.description),
      description: form.description.trim(),
      due_at,
      has_time: form.has_time,
      priority: form.priority,
      assignee_uuids: form.assignee_uuids,
      linked_user_uuid: linkedUuid,
      linked_user_role: linkedRoleResolved,
      label_uuids: form.label_uuids,
      alerts
    };
    setSubmitting(true);
    Promise.resolve(onSubmit(payload)).finally(() => setSubmitting(false));
  };

  /* ---------- Dropdown options ---------- */

  const priorityOptions = [
    ...PRIORITY_OPTIONS.map((p) => ({ value: p, label: p })),
    { value: 'None', label: 'None' }
  ];

  // The current user is labelled "Self" rather than their name/email, so the
  // common case ("assigned to me") reads fluently in the trigger. Other team
  // members keep their normal name + email display.
  const assigneeOptions = team.map((m) => ({
    value: m.uuid,
    label: selfUuid && m.uuid === selfUuid ? 'Self' : m.name,
    sublabel: m.email
  }));

  const linkedUserOptions = linkedUsers.map((u) => ({
    value: u.uuid,
    label: u.name,
    sublabel: u.email,
    avatar: u.avatar,
    role: u.role,
    email: u.email
  }));

  // Avatar element used by both the trigger (small) and the dropdown rows (larger).
  const renderAvatar = (opt, size) => {
    const px = `${size}px`;
    const cls = `shrink-0 rounded-full object-cover`;
    if (opt?.avatar) {
      return (
        <img
          src={opt.avatar}
          alt=""
          className={cls}
          style={{ width: px, height: px, aspectRatio: '1 / 1' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }
    const initial = (opt?.label || '?').trim().charAt(0).toUpperCase();
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-600"
        style={{ width: px, height: px, aspectRatio: '1 / 1', fontSize: Math.round(size * 0.45) }}
      >
        {initial}
      </span>
    );
  };

  // Compact avatar for the closed trigger.
  const renderLinkedUserIcon = (opt) => renderAvatar(opt, 20);

  // Two-line row in the dropdown: avatar on the left, name + email stacked
  // on the right. Padding inside this node bumps the row height beyond the
  // SearchableSelect default.
  const renderLinkedUserRow = (opt, selected) => (
    <div className="flex w-full items-center gap-3 py-1">
      {renderAvatar(opt, 32)}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-800">{opt.label}</div>
        {opt.email ? <div className="truncate text-xs text-gray-500">{opt.email}</div> : null}
      </div>
      {selected ? <span className="text-xs text-green-600">✓</span> : null}
    </div>
  );

  const labelOptions = labels.map((l) => ({
    value: l.uuid,
    label: l.name,
    color: l.color
  }));

  const handleCreateLabel = (name) =>
    create_label(name).then((created) => {
      if (!created) return null;
      setLabels((prev) => (prev.some((l) => l.uuid === created.uuid) ? prev : [...prev, created]));
      return { value: created.uuid, label: created.name, color: created.color };
    });

  return (
    <form
      onSubmit={handleSubmit}
      className="reminders-scope flex flex-col items-stretch gap-3.5 text-left"
    >
      {/* Description — first line becomes the task title */}
      <div className="w-full">
        <SectionLabel icon={FaRegStickyNote}>What needs doing?</SectionLabel>
        <textarea
          autoFocus
          rows={5}
          style={{
            border: '1.5px solid #cbd5e1',
            borderRadius: 6,
            minHeight: 120,
            width: '100%',
            padding: '10px 12px',
            fontSize: 14,
            color: '#1e293b',
            background: '#ffffff',
            outline: 'none',
            resize: 'vertical'
          }}
          placeholder="e.g. Call Priya to confirm Saturday class — she works late so call after 6pm IST"
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
        />
        <div className="mt-1 text-[10px] text-gray-400">First line becomes the reminder title.</div>
      </div>

      {/* Due date + (optional) time row */}
      <div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <SectionLabel icon={FaRegCalendarAlt}>Date</SectionLabel>
            <input
              type="date"
              className={`${inputCls} h-[34px] w-[170px] py-0`}
              value={form.due_date}
              onChange={(e) => update({ due_date: e.target.value })}
            />
          </div>
          <div className="flex flex-col">
            <SectionLabel>Time</SectionLabel>
            {form.has_time ? (
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  className={`${inputCls} h-[34px] w-[130px] py-0`}
                  value={form.due_time}
                  onChange={(e) => update({ due_time: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => update({ has_time: false })}
                  className="reminders-icon-btn inline-flex h-7 w-7 items-center justify-center"
                  title="Remove time"
                  aria-label="Remove time"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => update({ has_time: true })}
                className="reminders-dashed-btn inline-flex h-[34px] items-center gap-1 text-xs"
              >
                <FaPlus size={9} /> Add time
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Priority */}
      <div>
        <SectionLabel icon={FaFlag}>Priority</SectionLabel>
        <SearchableSelect
          value={form.priority}
          options={priorityOptions}
          onChange={(v) => update({ priority: v })}
          placeholder="Set priority"
          renderIcon={(opt) => <PriorityIcon priority={opt.value} />}
          renderRow={(opt, selected) => (
            <>
              <PriorityIcon priority={opt.value} />
              <span className="flex-1 truncate">{opt.label}</span>
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
      </div>

      {/* Assign to (multi) */}
      <div>
        <SectionLabel icon={FaUserCircle}>Assign to</SectionLabel>
        <SearchableSelect
          value={form.assignee_uuids}
          options={assigneeOptions}
          onChange={(v) => update({ assignee_uuids: v })}
          placeholder="Unassigned"
          multi
          clearable
          renderRow={(opt, selected) => (
            <>
              <FaUserCircle className="text-gray-400" size={13} />
              <span className="flex-1 truncate">{opt.label}</span>
              {opt.sublabel ? (
                <span className="text-[11px] text-gray-400">{opt.sublabel}</span>
              ) : null}
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
      </div>

      {/* Conflict warning */}
      {conflicts.length > 0 ? (
        <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-2.5 py-2 text-xs text-yellow-800">
          <FaExclamationTriangle className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">
              {conflicts.length} other task{conflicts.length > 1 ? 's' : ''} within 30 minutes for
              one of these team members.
            </div>
            <ul className="mt-0.5 list-disc pl-4">
              {conflicts.slice(0, 3).map((c) => (
                <li key={c.uuid}>
                  {c.title} — {moment(c.due_at).format('MMM D, h:mm A')}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {/* When opened from the dashboard (no locked user) we still need a
          way to pick the MYT user this task is about. */}
      {!lockedLinkedUser ? (
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <SectionLabel icon={FaLink}>Linked user</SectionLabel>
              {linkedSearching ? (
                <span className="text-[10px] font-medium uppercase tracking-wider text-mytPrimary">
                  Searching…
                </span>
              ) : null}
            </div>
            <div
              className="inline-flex overflow-hidden rounded-md border border-gray-300 text-[10px] font-semibold uppercase tracking-wider"
              role="group"
              aria-label="Search role"
            >
              {['student', 'teacher'].map((r) => {
                const active = linkedRole === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setLinkedRole(r);
                      // Keep only the currently-selected user when switching role
                      // so the dropdown isn't polluted with stale rows.
                      const selected =
                        lockedLinkedUser || linked_user_by_uuid(form.linked_user_uuid) || null;
                      setLinkedUsers(selected ? [selected] : []);
                    }}
                    // Inline styles beat the host's global `button { background:
                    // #cc5500 }` rule (which has !important via reminders.css
                    // helpers). The active state uses the MYT theme orange.
                    style={{
                      background: active ? '#ee731b' : '#ffffff',
                      color: active ? '#ffffff' : '#475569',
                      border: 'none',
                      margin: 0,
                      padding: '2px 10px'
                    }}
                    className="capitalize"
                    aria-pressed={active}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
          <SearchableSelect
            value={form.linked_user_uuid}
            options={linkedUserOptions}
            onChange={(v) => update({ linked_user_uuid: v })}
            onQueryChange={setLinkedQuery}
            asyncOptions
            renderIcon={renderLinkedUserIcon}
            renderRow={renderLinkedUserRow}
            placeholder={`Type a name to search ${linkedRole}s`}
            emptyLabel={
              linkedQuery.trim().length < 3
                ? 'Type 3+ characters to search'
                : linkedSearching
                  ? 'Searching…'
                  : 'No matches'
            }
            clearable
          />
        </div>
      ) : null}

      {/* Labels (multi + auto-create) */}
      <div>
        <SectionLabel icon={FaTag}>Labels</SectionLabel>
        <SearchableSelect
          value={form.label_uuids}
          options={labelOptions}
          onChange={(v) => update({ label_uuids: v })}
          placeholder="Add labels"
          multi
          onCreateOption={handleCreateLabel}
          renderRow={(opt, selected) => (
            <>
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: opt.color || '#e2e8f0' }}
              />
              <span className="flex-1 truncate">{opt.label}</span>
              {selected ? <span className="text-green-600 text-xs">✓</span> : null}
            </>
          )}
        />
      </div>

      {/* Alerts */}
      <div>
        <SectionLabel icon={FaRegBell}>Reminders (max 2)</SectionLabel>
        <div className="grid grid-cols-2 gap-3">
          <select
            className={inputCls}
            value={form.alert_one}
            onChange={(e) =>
              update({ alert_one: e.target.value === '' ? '' : Number(e.target.value) })
            }
          >
            <option value="">No reminder</option>
            {REMINDER_OFFSETS.map((r) => (
              <option key={`a1-${r.minutes}`} value={r.minutes}>
                {r.label}
              </option>
            ))}
          </select>
          <select
            className={inputCls}
            value={form.alert_two}
            onChange={(e) =>
              update({ alert_two: e.target.value === '' ? '' : Number(e.target.value) })
            }
          >
            <option value="">No second reminder</option>
            {REMINDER_OFFSETS.map((r) => (
              <option key={`a2-${r.minutes}`} value={r.minutes}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Submit row */}
      <div className="flex items-center justify-start gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting || !form.description.trim()}
          className="reminders-primary-btn text-sm font-medium"
        >
          {submitting ? 'Saving…' : effectiveSubmitLabel}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel} className="reminders-secondary-btn text-sm">
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}

export default ReminderForm;
